import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useCart } from "../context/CartContext";
import { toast } from "react-hot-toast";
import { FiTrash2, FiMinus, FiPlus } from "react-icons/fi";

const Cart = () => {
  const { items, removeFromCart, updateQuantity, clearCart } = useCart();

  const totalPrice = items.reduce(
    (total, item) => total + item.price * item.quantity,
    0
  );

  const discount = totalPrice * 0.1;
  const finalPrice = totalPrice - discount;

  const handleUpdateQuantity = (id, qty) => {
    if (qty < 1) {
      removeFromCart(id);
      toast.success("Item removed");
    } else {
      updateQuantity(id, qty);
    }
  };

  return (
    <div className="min-h-screen pt-24 pb-16 px-4 bg-gray-50 dark:bg-black">
      <div className="max-w-7xl mx-auto">

        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-12"
        >
          <h1 className="text-5xl font-bold font-playfair bg-gradient-to-r from-yellow-500 via-yellow-400 to-yellow-600 bg-clip-text text-transparent">
            Your Cart
          </h1>

          <p className="text-gray-500 text-lg mt-3">
            Review your selected luxury pieces
          </p>
        </motion.div>

        {items.length === 0 ? (
          <div className="text-center py-24">
            <div className="text-7xl mb-6">🛒</div>

            <h2 className="text-3xl font-bold mb-4">
              Your Cart is Empty
            </h2>

            <p className="text-gray-500 mb-10">
              Looks like you haven't added anything yet.
            </p>

            <Link
              to="/shop"
              className="px-10 py-4 rounded-xl bg-gradient-to-r from-yellow-500 to-yellow-600 text-black font-semibold shadow-xl hover:scale-105 transition"
            >
              Start Shopping
            </Link>
          </div>
        ) : (
          <div className="grid lg:grid-cols-3 gap-10">

            {/* CART ITEMS */}
            <div className="lg:col-span-2 space-y-6">

              {items.map((item) => (
                <motion.div
                  key={item._id}
                  initial={{ opacity: 0, y: 15 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-white dark:bg-gray-900 p-6 rounded-2xl shadow-xl flex gap-6"
                >

                  {/* IMAGE */}
                  <img
                    src={item.image}
                    alt={item.name}
                    className="w-32 h-32 rounded-xl object-cover"
                  />

                  {/* DETAILS */}
                  <div className="flex-1">

                    <h3 className="text-xl font-semibold mb-2">
                      {item.name}
                    </h3>

                    <p className="text-gray-500 mb-4">
                      Size: {item.selectedSize}
                    </p>

                    {/* QUANTITY */}
                    <div className="flex items-center gap-4">

                      <div className="flex items-center bg-gray-100 rounded-lg">

                        <button
                          onClick={() =>
                            handleUpdateQuantity(item._id, item.quantity - 1)
                          }
                          className="p-3 hover:bg-gray-200 rounded-l-lg"
                        >
                          <FiMinus />
                        </button>

                        <span className="px-4 font-semibold">
                          {item.quantity}
                        </span>

                        <button
                          onClick={() =>
                            handleUpdateQuantity(item._id, item.quantity + 1)
                          }
                          className="p-3 hover:bg-gray-200 rounded-r-lg"
                        >
                          <FiPlus />
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* PRICE */}
                  <div className="text-right">

                    <p className="text-2xl font-bold">
                      ₹{(item.price * item.quantity).toLocaleString()}
                    </p>

                    <button
                      onClick={() => {
                        removeFromCart(item._id);
                        toast.success("Removed from cart");
                      }}
                      className="mt-4 text-red-500 hover:text-red-700 flex items-center gap-1"
                    >
                      <FiTrash2 />
                      Remove
                    </button>
                  </div>
                </motion.div>
              ))}
            </div>

            {/* ORDER SUMMARY */}
            <div className="sticky top-24 h-fit">

              <div className="bg-white dark:bg-gray-900 rounded-2xl p-8 shadow-2xl">

                <h3 className="text-2xl font-bold mb-6">
                  Order Summary
                </h3>

                <div className="space-y-4">

                  <div className="flex justify-between">
                    <span>Subtotal</span>
                    <span>₹{totalPrice.toLocaleString()}</span>
                  </div>

                  <div className="flex justify-between text-green-600">
                    <span>Discount</span>
                    <span>-₹{discount.toLocaleString()}</span>
                  </div>

                  <div className="flex justify-between">
                    <span>Shipping</span>
                    <span className="text-green-600">Free</span>
                  </div>

                  <hr />

                  <div className="flex justify-between text-xl font-bold">
                    <span>Total</span>
                    <span>₹{finalPrice.toLocaleString()}</span>
                  </div>
                </div>

                {/* BUTTONS */}

                <Link
                  to="/checkout"
                  className="block w-full mt-8 py-4 text-center rounded-xl bg-gradient-to-r from-yellow-500 to-yellow-600 text-black font-semibold shadow-lg hover:scale-105 transition"
                >
                  Checkout
                </Link>

                <Link
                  to="/shop"
                  className="block w-full mt-4 py-4 text-center rounded-xl bg-black text-white font-semibold hover:bg-gray-900 transition"
                >
                  Continue Shopping
                </Link>

                <button
                  onClick={clearCart}
                  className="mt-6 w-full text-red-500 border border-red-300 py-3 rounded-lg hover:bg-red-50 transition"
                >
                  Clear Cart
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Cart;